<template>
  <div
    class="chat-window-container"
    aria-label="Netomi Chat AI Interface"
    tabindex="0"
    ref="chatWindowContainer"
    :class="[
      isChatboxactive ? 'chatbox-open' : '',
      isDisneyDemoBot ? 'disney-demo' : ''
    ]"
    :style="getStyles"
    @drop="(event) => this.dropHandler(event)"
    @dragover="(event) => this.dragOverHandler(event)"
    @dragleave="(event) => this.handleDragLeave(event)"
  >
    <Preloader v-if="!isWidgetConfigLoaded"></Preloader>
    <EndChat v-if="isWidgetConfigLoaded && showEndChat"></EndChat>
    <HighPriorityAlerts
      v-if="isWidgetConfigLoaded && showHighPriorityAlert"
    ></HighPriorityAlerts>
    <TermsAndConditions
      v-if="isWidgetConfigLoaded && showTermsAndConditions"
    ></TermsAndConditions>
    <MediumPriorityAlerts
      v-if="isWidgetConfigLoaded && showMediumPriorityAlert"
    ></MediumPriorityAlerts>
    <SurveyForm
      v-if="showSurvey"
      :showOnlySurvey="true"
      :message="getSurveyMessage"
      :isFirstResponse="getIsSurveyFirstResponse"
      :isSurveySubmitted="getIsSurveySubmitted"
      :submittedValues="getSurveySubmittedValues"
    />
    <Header v-if="isWidgetConfigLoaded && !hideHeader"></Header>
    <Banner v-if="isFormSubmissionBanner"></Banner>
    <Banner v-if="showBanner && isWidgetConfigLoaded"></Banner>
    <Overlay v-if="isDragDropActive && isWidgetConfigLoaded">
      <slot name="content">
        <div class="drop-area-container-overlay" />
        <div class="drop-area-container">
          <div class="drop-area-icon-container">
            <BaseIcon name="Squircle" />
            <img :src="getGenericDocIcon" alt="icon" />
            <p v-t="getDropAreaText"></p>
          </div>
        </div>
      </slot>
    </Overlay>
    <div
      class="chat-window-inner-container"
      :class="{ 'inner-partitions': isNonActionableNBAValidWithDelay }"
      v-if="isWidgetConfigLoaded"
    >
      <div
        class="chat-window-partition"
        :class="{
          'chat-window-left-partition': isNonActionableNBAValidWithDelay
        }"
      >
        <VisitorInfoForm v-if="showVisitorInfoForm" />
        <ViewVisitorInfo v-if="isValidVisitorInfo" />
        <SourcesOverlay v-if="isSourcesOverlayEnabled" />
        <NBAOverlay v-if="isNbaOverlayEnabled" />
        <Overlay v-show="isUserInactive"></Overlay>
        <ChatView
          v-show="!(isSourcesOverlayEnabled || isNbaOverlayEnabled)"
          :key="clearConversation"
          :showBlankScreen="isAlertOrTermsActive > 0"
        >
        </ChatView>
        <QuickMenu
          v-click-outside="handleOutsideClick"
          v-if="showQuickMenu"
        ></QuickMenu>
        <HeaderMenuDrawer
          v-click-outside="handleDrawerOutsideClick"
          v-if="showDrawerHeader"
        />
        <Footer
          v-show="
            !(
              isSourcesOverlayEnabled ||
              isNbaOverlayEnabled ||
              showDrawerHeader
            )
          "
        ></Footer>
      </div>
      <Transition name="slide">
        <div
          v-if="isNonActionableNBAValid"
          class="non-actionable-nba-container"
        >
          <p class="nba-header-label">{{ getHeading }}</p>
          <NonActionableNBALink
            :linkData="getNonActionableNBALinks"
            :staticLinkData="getNbaStaticLinks"
          />
        </div>
      </Transition>
    </div>
  </div>
</template>

<script>
import { defineAsyncComponent } from 'vue'
const Header = defineAsyncComponent(
  () => import(/* webpackChunkName: "header" */ './header')
)
const ChatView = defineAsyncComponent(
  () => import(/* webpackChunkName: "chat-view" */ './chat-view')
)
const Footer = defineAsyncComponent(
  () => import(/* webpackChunkName: "footer" */ './footer')
)
const NonActionableNBALink = defineAsyncComponent(
  () =>
    import(
      /* webpackChunkName: "non-actionable-nba-link" */ './chat-view/components/utilities/NonActionableNBALink.vue'
    )
)
import Overlay from './chat-view/components/utilities/Overlay.vue'
import EndChat from './chat-view/components/utilities/EndChat.vue'
import SourcesOverlay from './chat-view/components/utilities/SourcesOverlay.vue'
import NBAOverlay from './chat-view/components/utilities/NBAOverlay.vue'
import Banner from './components/Banner/index.vue'
import QuickMenu from './footer/components/QuickMenu.vue'
import Preloader from './chat-view/components/utilities/Preloader'
import { constantStrings } from '@/services/constants'
import SurveyForm from './chat-view/components/utilities/SurveyForm/index.vue'
import HeaderMenuDrawer from './header/HeaderMenuDrawer.vue'
import { uploadFile } from '../services/utils'
import BaseIcon from '@/views/BaseIcon.vue'
import AppWarn from '../services/errors/app-warns'
import HighPriorityAlerts from './chat-view/components/utilities/HighPriorityAlerts.vue'
import MediumPriorityAlerts from './chat-view/components/utilities/MediumPriorityAlerts.vue'
import TermsAndConditions from './chat-view/components/utilities/TermsAndConditions.vue'
import VisitorInfoForm from './chat-view/components/utilities/VisitorInfoForm.vue'
import ViewVisitorInfo from './chat-view/components/utilities/ViewVisitorInfo.vue'

export default {
  components: {
    Header,
    ChatView,
    Footer,
    Overlay,
    SourcesOverlay,
    Banner,
    QuickMenu,
    NBAOverlay,
    NonActionableNBALink,
    Preloader,
    EndChat,
    SurveyForm,
    HeaderMenuDrawer,
    BaseIcon,
    HighPriorityAlerts,
    MediumPriorityAlerts,
    TermsAndConditions,
    VisitorInfoForm,
    ViewVisitorInfo
  },
  data() {
    return {
      isUserInactive: false,
      isNonActionableNBAValidWithDelay: false,
      timeoutId: '',
      showSurvey: false,
      isDragDropActive: false
    }
  },
  mounted() {
    this.$refs['chatWindowContainer'].focus()
  },
  watch: {
    '$store.state.isOverlayActive': function () {
      this.isUserInactive = this.$store.state.isOverlayActive
    },
    '$store.state.isMqttConnected': function () {
      if (this.isWidgetConfigLoaded) {
        this.$store.dispatch('fetchHistory')
        this.$store.dispatch('triggerProActiveGreetings')
      }
    },
    isWidgetConfigLoaded: function (newVal) {
      if (newVal && this.$store.getters.isKnowledgeAiEnabled) {
        this.$store.dispatch('toggleChatWindowStatus', true)
        this.$store.dispatch('updateUnreadMessagesState', { enabled: false })
      }
    },
    isNonActionableNBAValid(newVal, oldVal) {
      this.timeoutId && clearTimeout(this.timeoutId)
      if (newVal) {
        this.isNonActionableNBAValidWithDelay = true
      } else if (!newVal && oldVal) {
        //done so that flex css remain intact for a while to show transition
        const id = setTimeout(() => {
          this.isNonActionableNBAValidWithDelay = false
        }, 500)
        this.timeoutId = id
      }
    },
    isSurveyValid(newVal) {
      this.showSurvey = newVal
    },
    isAlertOrTermsActive: {
      handler: function (newVal, oldVal) {
        const triggerType =
          this.$store.state.alerts.triggerGreeting ||
          this.$store.state.termsAndConditions.triggerGreeting
        if (newVal === 0 && oldVal !== 0 && triggerType) {
          if (triggerType === 'proactive') {
            this.$store.dispatch('triggerProActiveGreetings')
          } else if (triggerType === 'calloutbubble') {
            this.$store.dispatch('triggerCalloutBubbleGreetings')
          }
          this.$store.dispatch('updateGreetingTriggerOnAlertClosureState', '')
          this.$store.dispatch('updateGreetingTriggerOnTermsClosureState', '')
        }
        const quickReply = this.$store.state.termsAndConditions.quickReply
        if (newVal === 0 && oldVal !== 0 && quickReply) {
          this.$store.dispatch('sendMessage', quickReply)
          this.$store.dispatch('updateQuickReplyOnTermsClosureState', null)
        }
      }
    }
  },
  computed: {
    isAlertOrTermsActive() {
      return (
        this.showHighPriorityAlert ||
        this.showMediumPriorityAlert ||
        this.showTermsAndConditions
      )
    },
    showHighPriorityAlert() {
      return Object.keys(this.$store.state.alerts.highPriorityAlert)?.length
    },
    showMediumPriorityAlert() {
      return (
        !this.isSurveyValid &&
        !this.showHighPriorityAlert &&
        !this.showTermsAndConditions &&
        Object.keys(this.$store.state.alerts.mediumPriorityAlert)?.length
      )
    },
    showTermsAndConditions() {
      return (
        !this.isSurveyValid &&
        !this.showHighPriorityAlert &&
        Object.keys(this.$store.state.termsAndConditions.terms)?.length
      )
    },
    getGenericDocIcon() {
      return `${this.$cdn}/images/generic-doc.svg`
    },
    getDropAreaText() {
      return constantStrings.DROP_AREA_TEXT_CW
    },
    isMultiAttachmentEnabled() {
      return this.$store.state.widgetConfig.fileSharing?.multiAttachment
    },
    isFileSharingEnabled() {
      return this.$store.state.widgetConfig.fileSharing?.isEnabled
    },
    isSurveyValid() {
      return this.$store.state.surveyFormState.isValid
    },
    getSurveyMessage() {
      return this.$store.state.surveyFormState.data
    },
    getIsSurveySubmitted() {
      return this.$store.state.surveyFormState.isSurveySubmitted || false
    },
    getSurveySubmittedValues() {
      return this.$store.state.surveyFormState.submittedValues || {}
    },
    getIsSurveyFirstResponse() {
      return this.$store.state.surveyFormState.isFirstResponse
    },
    isFormSubmissionBanner() {
      return this.$store.state.formSubmissionStatus.loader === true
    },
    isSourcesOverlayEnabled() {
      return (
        this.$store.state.knowledgeAISourcesState.isEnabled &&
        !this.$store.state.isChatWidgetFullScreen
      )
    },
    isDisneyDemoBot() {
      return this.$store.state.widgetConfig?.brandGPT?.enable || false
    },
    getStyles() {
      if (this.isDisneyDemoBot) {
        const bgImage =
          this.$store.state.widgetConfig?.brandGPT?.backgroundImage
        return { backgroundImage: `url(${bgImage})` }
      }

      return {}
    },
    clearConversation() {
      return this.$store.state.clearConversationState.clearConversation
    },
    showBanner() {
      return !this.$store.state.connectionStatus
    },
    isChatboxactive() {
      return this.$store.state.isWindowMaximized
    },
    showQuickMenu() {
      return this.$store.state.quickMenuState.visible
    },
    showDrawerHeader() {
      return this.$store.state.headerDrawerState.show
    },
    isWidgetConfigLoaded() {
      return (
        Object.keys(this.$store.state.widgetConfig)?.length > 0 &&
        this.$store.state.isMqttConnected
      )
    },
    isNbaOverlayEnabled() {
      return (
        this.$store.state.nonActionableNBAOverlayEnabled &&
        !this.$store.state.isChatWidgetFullScreen
      )
    },
    isNonActionableNBAValid() {
      return (
        this.$store.state.isChatWidgetFullScreen &&
        (this.$store.state?.nonActionableNBAOptions?.length ||
          this.$store.state?.widgetConfig?.nbaConfig?.staticLinks?.length)
      )
    },
    getNonActionableNBALinks() {
      return this.$store.state.nonActionableNBAOptions
    },
    getHeading() {
      return (
        this.$store.state?.widgetConfig?.nbaConfig?.heading ||
        constantStrings.ARTICLES_OFFERS
      )
    },
    getNbaStaticLinks() {
      return this.$store.state?.widgetConfig?.nbaConfig?.staticLinks
    },
    showEndChat() {
      return this.$store.state.showEndChatFlow.enableWidget
    },
    showVisitorInfoForm() {
      return this.$store.state.visitorInfoForm?.isOpen
    },
    isValidVisitorInfo() {
      return this.$store.state.visitorInfoForm?.visitorInfoDetails
    },
    hideHeader() {
      return this.$store.getters.isResponseAnalyzerEnabled
    }
  },
  methods: {
    isFormDragDropPresent() {
      return document.getElementsByClassName('drag-drop-wrapper-active').length
    },
    handleOutsideClick() {
      this.$store.dispatch('updateQuickMenuVisibility', false)
    },
    handleChatWindowClose() {
      this.$store.dispatch('updateEndChatModalStatus', false)
    },
    handleDrawerOutsideClick() {
      this.$store.dispatch('toggleShowHeaderDrawer')
    },
    handleDragLeave(event) {
      const rect = this.$refs['chatWindowContainer'].getBoundingClientRect()
      const isOutside =
        event.clientY < rect.top ||
        event.clientY >= rect.bottom ||
        event.clientX < rect.left ||
        event.clientX >= rect.right
      if (isOutside) {
        this.handleMouseLeave()
      }
    },
    handleMouseLeave() {
      if (this.isDragDropActive) {
        const droppableElement = this.$refs['chatWindowContainer']
        droppableElement.classList.remove('drag-drop-active')
        this.isDragDropActive = false
      }
    },
    dragOverHandler(event) {
      event.preventDefault()
      if (this.isFileSharingEnabled && !this.isFormDragDropPresent()) {
        const droppableElement = this.$refs['chatWindowContainer']
        droppableElement.classList.add('drag-drop-active')
        this.isDragDropActive = true
      }
    },
    dropHandler(event) {
      try {
        event.preventDefault()
        this.handleMouseLeave()
        if (!this.isDropValid()) {
          return
        }
        const dataItems = event.dataTransfer.items
        if (
          dataItems &&
          !(dataItems.length > 1 && !this.isMultiAttachmentEnabled)
        ) {
          // eslint-disable-next-line no-extra-semi
          ;[...dataItems].forEach((item) => {
            // If dropped items aren't files, reject them
            if (item.kind === 'file') {
              uploadFile(item.getAsFile())
            }
          })
        }
      } catch (error) {
        console.log('Error While Uploading', error)
        new AppWarn({
          name: 'Action',
          message: `Error while uploading files: ${error}`,
          error
        })
      } finally {
        // clear the target value
        event.target.value = ''
      }
    },
    isDropValid() {
      return (
        this.isFileSharingEnabled &&
        !this.isFormDragDropPresent() &&
        !(
          this.$store.state.inputFieldDisableStatus ||
          this.$store.state.inputFieldTempDisableStatus ||
          this.$store.state.inputFieldStreamingDisableStatus ||
          this.$store.state.footer.disableInputOnSend.state ||
          this.$store.state.widgetConfig.footerStatus ===
            constantStrings.DISABLED
        )
      )
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/css/theme.scss';
@import '@/styles/css/commonStyles.scss';

.chat-window-container {
  flex: 1;
  background: #fff;
  @include border-radius(var(--radius-chat-outer-curve));
  overflow: hidden;
  @include display-flex;
  flex-direction: column;
  opacity: 0;
  visibility: hidden;
  transform: scale(0);
  transition: 0.3s ease-in-out;
  transform-origin: bottom right;

  .chat-window-inner-container {
    display: contents;

    &.inner-partitions {
      display: flex;
      flex: 1;
      overflow: hidden;

      .non-actionable-nba-container {
        width: 30%;
        height: 100%;
        padding-top: 28px;
        overflow: auto;
        animation: slideOut 0.5s forwards;
        padding-left: 32px;
        border-left: 1px solid #d9d9d9;
        margin: 0px 32px 10px auto;
        scrollbar-width: thin;
        scrollbar-color: #c1c1c1 #fafafa;
        &::-webkit-scrollbar {
          width: 16px;
          background-clip: content-box;
          display: none;
          transition: 0.3s all ease-in-out;
        }
        &::-webkit-scrollbar-track {
          background-color: #fafafa;
        }
        &::-webkit-scrollbar-thumb {
          background-color: #c1c1c1;
          width: 16px;
          height: 30px;
          border-radius: 10px;
          border: 4px solid #fafafa;
        }
        &:hover::-webkit-scrollbar {
          display: block;
          transition: 0.3s ease-in-out;
        }

        .nba-header-label {
          font-size: 18px;
          font-weight: 500;
          line-height: 22px;
        }

        :deep(.nba-non-actionable-link-wrapper) {
          padding-bottom: 30px;
        }
        @keyframes slideOut {
          from {
            transform: translateX(50px);
            opacity: 0;
          }
          to {
            transform: translateX(0%);
            opacity: 1;
          }
        }
      }
    }

    .chat-window-partition {
      display: contents;
      animation-duration: 0.5s;
      animation-fill-mode: forwards;

      &.chat-window-left-partition {
        display: flex;
        flex-direction: column;
        flex: 1;
        animation-name: expandedAnimation;
      }
      @keyframes expandedAnimation {
        from {
          transform: translateX(50px);
        }
        to {
          transform: translateX(0px);
        }
      }
    }
  }
  &.chatbox-open {
    opacity: 1;
    visibility: visible;
    transform: scale(1);
  }

  &.disney-demo {
    margin: 0 !important;
    border-radius: 0 !important;
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;

    :deep(.chat-list-wrapper) {
      width: 100% !important;
      background-color: transparent !important;
    }

    :deep(.preloader) {
      background-color: transparent !important;

      .loader {
        width: 72px;
        height: 72px;
      }
    }

    :deep(.typing) {
      background: transparent !important;
    }

    :deep(.time-stamp-hover) {
      background-color: transparent !important;
      font-size: 14px !important;
    }

    :deep(.last-message-status) {
      font-size: 14px !important;
    }

    :deep(.message-list-wrapper),
    :deep(.end-chat-wrapper),
    :deep(.typing-indicator) {
      width: 80% !important;
      margin: 0 auto !important;
    }

    :deep(.msg-spacer-blk) {
      padding: 0 16px 16px !important;
    }

    :deep(.quick-reply-wrapper) {
      width: 80% !important;
      margin: 0 auto !important;
      padding: 8px 28px 8px 0 !important;

      .quick-reply-container {
        .quick-reply-button {
          font-size: 18px !important;
          color: #164aee !important;
          border-color: #164aee !important;
          box-shadow:
            0px 0.3px 0.9px rgba(0, 0, 0, 0.1),
            0px 1.6px 3.6px rgba(0, 0, 0, 0.13) !important;
        }
      }
    }

    :deep(.ai-response-block) {
      .icon-logo {
        width: 32px !important;
        height: 32px !important;
        margin-right: 12px !important;
      }
      .text-messages {
        background: linear-gradient(
          180deg,
          #f8f9fc 0%,
          #f4f7fd 100%
        ) !important;
        box-shadow:
          0px 0.3px 0.9px rgba(0, 0, 0, 0.1),
          0px 1.6px 3.6px rgba(0, 0, 0, 0.13) !important;
        color: #313343 !important;
      }
    }

    :deep(.user-response-block .text-messages) {
      background: var(--disney-gradient-color) !important;
      box-shadow:
        0px 0.3px 0.9px rgba(0, 0, 0, 0.1),
        0px 1.6px 3.6px rgba(0, 0, 0, 0.13);
    }

    :deep(.text-messages) {
      max-width: 80% !important;

      .text {
        font-size: 20px !important;
        padding: 16px 24px !important;
      }
    }

    :deep(.content-container) {
      &:not(:first-child) {
        margin-top: 16px;
      }
    }

    :deep(.menu-options-modal) {
      width: 100% !important;
      position: fixed;
      bottom: 0;

      .menu-header {
        padding: 16px 24px !important;
        .heading {
          font-size: 20px !important;
        }
      }

      .menu-options {
        padding: 16px 24px !important;
        list-style-type: none !important;
        .menu-option {
          font-size: 20px !important;
          margin: 0 !important;
          padding: 0 !important;
          border: 0 !important;
          color: rgb(0, 0, 0) !important;
        }
      }
    }

    :deep(.widget-footer-fullscreen) {
      width: 80% !important;
    }

    :deep(.icon-send) {
      width: 28px !important;
      height: 28px !important;
      box-shadow:
        0px 0.3px 0.9px rgba(0, 0, 0, 0.1),
        0px 1.6px 3.6px rgba(0, 0, 0, 0.13) !important;
      border-radius: 37px !important;
      background: var(--disney-gradient-color) !important;
      margin: 0 0 0 16px !important;
      cursor: pointer !important;
    }

    #quick-menu-options-btn {
      width: 48px !important;
      height: 48px !important;
      box-shadow:
        0px 0.3px 0.9px rgba(0, 0, 0, 0.1),
        0px 1.6px 3.6px rgba(0, 0, 0, 0.13) !important;
      background: #fff !important;
      border-radius: 37px !important;
      cursor: pointer !important;
      margin: 0 16px 0 0 !important;
      display: flex;
      align-items: center;
      justify-content: center;

      .quick-menu-icon {
        margin: 0 !important;
      }
    }

    :deep(.custom-textarea) {
      background: #ffffff !important;
      border-radius: 100px !important;
      box-shadow:
        0px 0.3px 0.9px rgba(0, 0, 0, 0.1),
        0px 1.6px 3.6px rgba(0, 0, 0, 0.13) !important;
      border: 0.25px solid rgba(0, 0, 0, 0.22) !important;
      font-weight: 500 !important;
      line-height: 24px !important;
      color: #6d6d6d !important;
      font-size: 20px !important;
      padding: 16px 48px !important;
    }
  }
  @media screen and (max-device-width: 767px) {
    @include round(0);
    margin: 0;
  }
}
.drop-area-container-overlay {
  height: 100%;
  width: 100%;
  opacity: 0.3;
  background: #000000;
}
.drop-area-container {
  position: absolute;
  .drop-area-icon-container {
    position: relative;
    font-weight: 600;
    font-size: 16px;
    line-height: 20px;
    gap: 11px;
    color: #ffffff;
    :deep(svg) {
      position: absolute;
      left: 32%;
      top: 50%;
      transform: translateY(-30%);
    }
    img {
      position: absolute;
      left: 38%;
    }
    p {
      transform: translateY(350%);
    }
  }
}
.overlay {
  background: rgba(255, 255, 255, 0.5);
  -webkit-filter: blur(7px);
  margin-top: 5px;
}
.slide-enter-active,
.slide-leave-active {
  transition: width 0.5s ease;
}
.slide-leave-from {
  width: 30%;
}

.slide-leave-to {
  width: 30px !important;
}
</style>
